import type { Project } from "@/features/leaderboard/types/index.ts";
import { useStealthWatchlist } from "../hooks/use-intelligence-queries.ts";
import { exportCsv } from "../lib/csv-export.ts";
import { ProjectNameLink } from "./ProjectNameLink.tsx";

interface StealthWatchlistProps {
  onOpenProjectDetail?: (project: Project) => void;
}

export function StealthWatchlist({ onOpenProjectDetail }: StealthWatchlistProps = {}) {
  const { data, isLoading, isError } = useStealthWatchlist(50);

  if (isLoading && !data) return <div className="intel-loading">Loading stealth watchlist…</div>;
  if (isError && !data) return <div className="intel-error">Failed to load stealth watchlist</div>;
  if (!data) return null;

  const handleExport = () => {
    exportCsv(
      "intelligence-stealth-watchlist.csv",
      ["Project", "Twitter handle", "Family", "Score", "Investors", "Top investors (first 5)"],
      data.map((r) => [
        r.name ?? r.twitterHandle,
        r.twitterHandle,
        r.family ?? "Other",
        r.alphaScore.toFixed(1),
        r.investorCount,
        r.topInvestors.join(" | "),
      ])
    );
  };

  return (
    <section className="intel-section">
      <header className="intel-section__header">
        <div className="intel-section__header-row">
          <div>
            <h3 className="intel-section__title">Stealth watchlist</h3>
            <p className="intel-section__subtitle">
              Projects with alpha score ≥ 30 AND at least one canonical investor committed, but{" "}
              <strong>no public raise amount reported</strong>. Typically means the round is either
              still live, recently closed but undisclosed, or in a quiet window — the single
              highest-leverage BD class. Sorted by score, top 50 shown.
            </p>
          </div>
          {data.length > 0 && (
            <button
              type="button"
              className="intel-csv-btn"
              onClick={handleExport}
              aria-label="Download stealth watchlist as CSV"
            >
              Download CSV
            </button>
          )}
        </div>
      </header>

      {data.length === 0 ? (
        <div className="intel-coinv-empty">No projects match the stealth criteria right now.</div>
      ) : (
        <div className="intel-stealth-wrap">
          <table className="intel-stealth">
            <thead>
              <tr>
                <th>Project</th>
                <th>Family</th>
                <th className="intel-stealth__num-head">Score</th>
                <th className="intel-stealth__num-head">Investors</th>
                <th>Top backers</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.projectId}>
                  <td className="intel-stealth__project">
                    <ProjectNameLink
                      projectId={row.projectId}
                      label={row.name ?? row.twitterHandle}
                      onOpenProjectDetail={onOpenProjectDetail}
                    />
                  </td>
                  <td className="intel-stealth__family">{row.family ?? "Other"}</td>
                  <td className="intel-stealth__num">{row.alphaScore.toFixed(1)}</td>
                  <td className="intel-stealth__num">{row.investorCount}</td>
                  <td className="intel-stealth__investors">
                    {row.topInvestors.join(", ")}
                    {row.investorCount > row.topInvestors.length &&
                      ` +${row.investorCount - row.topInvestors.length} more`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
